import { useEffect } from "react";
import { useLocation, matchRoutes } from "react-router-dom";
import appRoutes from "routes";
import { useSelector } from "react-redux";
import Clarity from "@microsoft/clarity";
import { FEATURE_FLAGS } from "config/feature-flags";
import { getUser } from "store/selector";
import {
  getPersonalInfo,
  getBirthDetails,
  getResidentialDetails,
} from "view/profile/store/selector";
import {
  trackPageView,
  identifyUser,
  getDeviceInfo,
  calculateAge,
} from "utils/analytics";

const AnalyticsTracker = () => {
  const location = useLocation();
  const { userId, username } = useSelector(getUser);
  const personalInfo = useSelector(getPersonalInfo);
  const birthDetails = useSelector(getBirthDetails);
  const residentialDetails = useSelector(getResidentialDetails);

  useEffect(() => {
    // Initialize Clarity
    if (
      FEATURE_FLAGS.ENABLE_CLARITY &&
      process.env.REACT_APP_CLARITY_PROJECT_ID
    ) {
      Clarity.init(process.env.REACT_APP_CLARITY_PROJECT_ID);
    }
  }, []);

  // ─────────────────────────────────────────────
  //  Set GA4 User Properties when user logs in
  //  or profile data changes
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (userId) {
      // Basic user info
      const userProps = {
        username,
        user_type: "registered",
      };

      // Gender (Groom / Bride)
      if (personalInfo?.iAm) {
        userProps.gender = personalInfo.iAm;
      }

      // Age (calculated from DOB)
      const dob = birthDetails?.dob?.date;
      const age = calculateAge(dob);
      if (age) {
        userProps.age = age;
        // Age bracket for easier segmentation in GA4 reports
        if (age < 25) userProps.age_group = "18-24";
        else if (age < 30) userProps.age_group = "25-29";
        else if (age < 35) userProps.age_group = "30-34";
        else if (age < 40) userProps.age_group = "35-39";
        else userProps.age_group = "40+";
      }

      // Marital status
      if (personalInfo?.maritalStatus) {
        userProps.marital_status = personalInfo.maritalStatus;
      }

      // Location
      if (residentialDetails?.hometown) {
        userProps.city = residentialDetails.hometown;
      }
      if (residentialDetails?.stateName) {
        userProps.state = residentialDetails.stateName;
      }

      // Device / platform info
      // NOTE: GA4 auto-collects device category, OS, browser in built-in
      // dimensions. Setting them as user properties allows easier
      // custom segmentation in Explore reports and audiences.
      const deviceInfo = getDeviceInfo();
      userProps.device_type = deviceInfo.device_type; // mobile / tablet / desktop
      userProps.operating_system = deviceInfo.os; // Android / iOS / Windows / macOS
      userProps.browser = deviceInfo.browser; // Chrome / Safari / Firefox / Edge
      userProps.screen_resolution = deviceInfo.screen_resolution; // e.g. 1920x1080

      identifyUser(userId, userProps);

      // Identify user in Clarity
      if (FEATURE_FLAGS.ENABLE_CLARITY) {
        Clarity.identify(userId, null, null, username);
      }
      // Note: identify signature is (customId, customSessionId, customPageId, friendlyName)
      // I am mapping userId -> customId and username -> friendlyName
    }
  }, [
    userId,
    username,
    personalInfo?.iAm,
    personalInfo?.maritalStatus,
    birthDetails?.dob?.date,
    residentialDetails?.hometown,
    residentialDetails?.stateName,
  ]);

  // ─────────────────────────────────────────────
  //  Track page views on SPA route changes
  // ─────────────────────────────────────────────
  useEffect(() => {
    // Determine title and screen name dynamically from the route config
    const getPageDetails = (pathname) => {
      const allRoutes = Object.values(appRoutes).flat();
      const matches = matchRoutes(allRoutes, pathname);

      // If a route matched, extract the title and screen properties
      if (matches && matches.length > 0) {
        // Typically the matched route we care about is the last one in the matches array
        const matchedRoute = matches[matches.length - 1].route;
        if (matchedRoute.title && matchedRoute.screen) {
          return {
            title: matchedRoute.title,
            screen: matchedRoute.screen,
          };
        }
      }

      // Fallback
      return {
        title: document.title || "Kalyanawaran - Matrimonial Service",
        screen: pathname,
      };
    };

    // Track Page View with a small delay to let Helmet set document.title fallbacks if needed
    setTimeout(() => {
      const details = getPageDetails(location.pathname);
      trackPageView(
        location.pathname + location.search,
        details.title,
        details.screen,
        userId
      );
    }, 100);
  }, [location]);

  return null;
};

export default AnalyticsTracker;
